/**
 * Requirement overview — ECharts pie chart + status-grouped requirement IDs + version information.
 */

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, Tag, Typography, Spin, Alert, Empty, Pagination } from 'antd';
import type { RequirementOverviewResponse, RequirementVersion, PaginatedResponse } from '@/types';
import VersionCard from './VersionCard';
import { listVersions } from '@/api/versions';

const { Title, Text } = Typography;

// Lazy-load ECharts to avoid blocking initial bundle
let echarts: typeof import('echarts') | null = null;
const loadECharts = async () => {
  if (!echarts) {
    echarts = await import('echarts');
  }
  return echarts;
};

interface RequirementOverviewProps {
  overview: RequirementOverviewResponse | null;
  isLoading: boolean;
  error: string | null;
  projectId: number;
  onReqIdClick?: (reqId: string, subcategory?: string) => void;
}

export interface RequirementOverviewRef {
  refreshVersions: () => void;
}

const RequirementOverview = forwardRef<RequirementOverviewRef, RequirementOverviewProps>(({
  overview,
  isLoading,
  error,
  projectId,
  onReqIdClick,
}, ref) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);

  // Version data state
  const [versions, setVersions] = useState<RequirementVersion[]>([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [versionPagination, setVersionPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Fetch versions
  const fetchVersions = async (page: number = 1, pageSize: number = 20) => {
    setVersionLoading(true);
    setVersionError(null);

    try {
      const response: PaginatedResponse<RequirementVersion> = await listVersions(projectId, {
        page,
        page_size: pageSize,
        ordering: '-published_at',
      });

      setVersions(response.results);
      setVersionPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.count,
      });
    } catch (err: unknown) {
      console.error('Failed to fetch versions:', err);
      setVersionError(err instanceof Error ? err.message : '加载版本列表失败');
    } finally {
      setVersionLoading(false);
    }
  };

  // Fetch versions when projectId changes
  useEffect(() => {
    if (!projectId) return;
    fetchVersions(1, versionPagination.pageSize);
  }, [projectId]);

  const handleVersionPageChange = (page: number, pageSize?: number) => {
    fetchVersions(page, pageSize || versionPagination.pageSize);
  };

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refreshVersions: () => {
      fetchVersions(1, versionPagination.pageSize);
    },
  }));

  useEffect(() => {
    if (!overview || !chartRef.current) return;

    let disposed = false;
    let handleResize: (() => void) | null = null;

    const renderChart = async () => {
      const ec = await loadECharts();
      if (disposed || !chartRef.current) return;

      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      const chart = ec.init(chartRef.current);
      chartInstance.current = chart;

      const data = overview.statusDistribution.map((item) => ({
        name: item.status,
        value: item.count,
      }));

      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { orient: 'vertical', left: 'left' },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            label: { show: true, formatter: '{b}: {c}' },
            data,
          },
        ],
      });

      handleResize = () => {
        if (chart && !disposed) {
          chart.resize();
        }
      };
      window.addEventListener('resize', handleResize);
    };

    renderChart();

    return () => {
      disposed = true;
      if (handleResize) {
        window.removeEventListener('resize', handleResize);
      }
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [overview]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon title="数据加载失败" description={error} />;
  }

  if (!overview) {
    return <Empty description="暂无总览数据" />;
  }

  return (
    <div>
      <Card className="mb-4">
        <Title level={5}>需求状态分布 (共 {overview.totalCount} 条)</Title>
        <div ref={chartRef} style={{ width: '100%', height: 350 }} />
      </Card>

      {overview.statusDistribution.map((item) => (
        <Card key={item.status} size="small" className="mb-3">
          <div className="flex items-center mb-2">
            <Tag>{item.status}</Tag>
            <Text type="secondary">{item.count} 条</Text>
          </div>
          <div className="flex flex-wrap gap-1">
            {item.requirementIds.map((rid) => (
              <Tag
                key={rid}
                className="cursor-pointer"
                onClick={() => onReqIdClick?.(rid)}
              >
                {rid}
              </Tag>
            ))}
          </div>
        </Card>
      ))}

      {/* Version Information Section */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
        <Title level={5} style={{ marginBottom: 16 }}>
          版本信息
        </Title>
        {versionLoading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        ) : versionError ? (
          <Alert type="error" title="版本加载失败" description={versionError} />
        ) : versions.length === 0 ? (
          <Empty description="暂无版本" />
        ) : (
          <>
            {versions.map((version, index) => (
              <VersionCard
                key={version.id}
                version={version}
                projectId={projectId}
                isLatest={index === 0 && versionPagination.current === 1}
                onDeleted={() => fetchVersions(1, versionPagination.pageSize)}
              />
            ))}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Pagination
                current={versionPagination.current}
                pageSize={versionPagination.pageSize}
                total={versionPagination.total}
                onChange={handleVersionPageChange}
                showSizeChanger
                showTotal={(total) => `共 ${total} 个版本`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

RequirementOverview.displayName = 'RequirementOverview';

export default RequirementOverview;
